import { TFunction } from '@lyearn/i18n';
import {
  ArchiveOutline,
  Calendar2Outline,
  ClockOutline,
  EditOutline,
  EyeOutline,
  FlashOutline,
  LayoutOutline,
  PlusFill,
  RefreshFill,
  SocialGoogleCalendar,
  SocialOutlook,
  Trash2Outline,
  Undo2Outline,
} from '@lyearn/icons';
import { ActionMenuItemType } from '@lyearn/molecules';
import UserEntity from '@/entity/User';
import currentUser from '@/helper/currentUser';
import { PublicMeetingInstanceStatusEnum, PublicMeetingStatusEnum, UserType } from '@/types/schema';

import { MeetingInstanceFragment } from '../graphql/fragment/generated/MeetingInstance';
import { MeetingListItemFragment } from '../graphql/fragment/generated/MeetingListItem';
import { ActionType, MenuItemActionType } from '../types';

export const isMeetingMissed = (meetingInstance: Pick<MeetingInstanceFragment, 'status'>) =>
  meetingInstance.status === PublicMeetingInstanceStatusEnum.Missed;

export const isMeetingLate = (meetingInstance: Pick<MeetingInstanceFragment, 'status'>) =>
  meetingInstance.status === PublicMeetingInstanceStatusEnum.Late;

export const getOrderedParticipants = (participants?: Pick<UserType, 'name' | 'id'>[] | null) => {
  // 1on1s are always between 2 people
  // Here, we want the logged-in user to be the last one in the array
  return participants?.[1].id === currentUser.id() ? participants : participants?.slice().reverse();
};

export const get1on1Name = (participants?: Pick<UserType, 'name' | 'id'>[] | null) => {
  if (!participants || participants.length <= 1) return '';

  const orderedParticipants = getOrderedParticipants(participants);

  return orderedParticipants && orderedParticipants.length > 1
    ? `${UserEntity.firstName(orderedParticipants[0])} x ${UserEntity.firstName(
        orderedParticipants[1],
      )}`
    : '';
};

export const getName = (participants?: Pick<UserType, 'name' | 'id'>[] | null) => {
  if (!participants || participants.length <= 1) return '';

  const orderedParticipants = getOrderedParticipants(participants);

  return orderedParticipants && orderedParticipants.length > 1
    ? `${UserEntity.firstName(orderedParticipants[0])}`
    : '';
};

export const getMeetingItemMenuitems = ({
  t,
  isOneOff,
}: {
  t: TFunction;
  isOneOff: boolean;
}): ActionMenuItemType[] => [
  {
    id: MenuItemActionType.RESCHEDULE,
    label: t('1on1s:Actions.Reschedule'),
    EndIcon: RefreshFill,
  },
  {
    id: MenuItemActionType.ADD_TO_CALENDAR,
    label: t('Common:Actions.AddToCalendar'),
    EndIcon: Calendar2Outline,
    enabled: isOneOff,
    items: [
      {
        id: MenuItemActionType.ADD_TO_CALENDAR_GOOGLE,
        label: t('Common:Actions.GoogleCalendar'),
        EndIcon: SocialGoogleCalendar,
      },
      {
        id: MenuItemActionType.ADD_TO_CALENDAR_OUTLOOK,
        label: t('Common:Actions.MicrosoftOutlook'),
        EndIcon: SocialOutlook,
      },
    ],
  },
  {
    id: MenuItemActionType.DELETE,
    type: 'confirmation',
    label: t('Common:Actions.Delete'),
    confirmLabel: t('Common:Actions.Sure'),
    appearance: 'danger',
    EndIcon: Trash2Outline,
    enabled: isOneOff,
  },
];

export const getOneOffMeetingMenuItems = ({ t }: { t: TFunction }) => [
  {
    id: ActionType.CREATE_ONE_OFF_NOW,
    label: t('1on1s:Actions.StartNow'),
    EndIcon: FlashOutline,
  },
  {
    id: ActionType.CREATE_ONE_OFF_LATER,
    label: t('1on1s:Actions.ScheduleLater'),
    EndIcon: ClockOutline,
  },
];

type Props = {
  t: TFunction;
  meeting?: MeetingListItemFragment;
  showView: boolean;
  is1on1: boolean;
};

export const getMeetingMenuItems = ({ t, meeting, showView, is1on1 }: Props) =>
  [
    {
      id: ActionType.SHOW_ONE_OFF_MENU,
      label: is1on1 ? t('1on1s:Actions.CreateOneOff') : t('1on1s:Actions.CreateOneOffMeeting'),
      EndIcon: PlusFill,
      enabled: meeting?.status === PublicMeetingStatusEnum.Active,
      items: getOneOffMeetingMenuItems({ t }),
    },
    {
      id: ActionType.VIEW,
      label: t('Common:Actions.View'),
      EndIcon: EyeOutline,
      enabled: showView,
    },
    {
      id: ActionType.EDIT,
      label: is1on1 ? t('1on1s:Actions.EditFrequency') : t('Common:Actions.Edit'),
      EndIcon: EditOutline,
      enabled: meeting?.status === PublicMeetingStatusEnum.Active,
    },
    {
      id: ActionType.TEMPLATE,
      label: meeting?.templateId
        ? t('1on1s:Actions.ChangeTemplate')
        : t('1on1s:Actions.SetTemplate'),
      EndIcon: LayoutOutline,
      enabled: meeting?.status === PublicMeetingStatusEnum.Active && is1on1,
    },
    {
      id: ActionType.ADD_TO_CALENDAR,
      label: t('Common:Actions.AddToCalendar'),
      EndIcon: Calendar2Outline,
      enabled: meeting?.status === PublicMeetingStatusEnum.Active,
      items: [
        {
          id: ActionType.ADD_TO_CALENDAR_GOOGLE,
          label: t('Common:Actions.GoogleCalendar'),
          EndIcon: SocialGoogleCalendar,
        },
        {
          id: ActionType.ADD_TO_CALENDAR_OUTLOOK,
          label: t('Common:Actions.MicrosoftOutlook'),
          EndIcon: SocialOutlook,
        },
      ],
    },
    {
      id: ActionType.REACTIVATE,
      label: t('Common:Actions.Reactivate'),
      EndIcon: Undo2Outline,
      enabled: meeting?.status === PublicMeetingStatusEnum.Deactivated,
    },
    {
      id: ActionType.DEACTIVATE,
      label: t('Common:Actions.Deactivate'),
      EndIcon: ArchiveOutline,
      appearance: 'danger',
      enabled: meeting?.status === PublicMeetingStatusEnum.Active,
    },
    {
      id: ActionType.DELETE,
      label: t('Common:Actions.Delete'),
      EndIcon: Trash2Outline,
      appearance: 'danger',
      enabled: meeting?.status === PublicMeetingStatusEnum.Deactivated,
    },
  ] as ActionMenuItemType[];
